import { PriceDisplay } from "@modules/shared/Product/PriceDisplay";
import {
  BasePriceLine,
  cheapestVariantBasePrice,
} from "@modules/shared/Product/BasePrice";
import { cn } from "@ui/lib/utils";
import { calculateDiscount, isDiscounted } from "@modules/shared/utils/utils";
import { Link } from "@tanstack/react-router";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { Image } from "@shopify/hydrogen-react";
import { useInView } from "framer-motion";
import { useMemo, useRef } from "react";
import type { Product } from "@shopify/hydrogen-react/storefront-api-types";

type Props = {
  product: Product;
  basePriceVariants?: Parameters<typeof cheapestVariantBasePrice>[0];
  productItemClassName?: string;
  isBigger?: boolean;
  productItemTitleClassName?: string;
  productPriceClassName?: string;
  productImageClassName?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageFetchPriority?: "high" | "low" | "auto" | "eager";
};

export const HomepageProductCard: React.FC<Props> = ({
  product,
  basePriceVariants = [],
  productItemClassName,
  isBigger,
  productItemTitleClassName,
  productPriceClassName,
  productImageClassName,
  imageWidth = 270,
  imageHeight = 360,
  imageFetchPriority,
}) => {
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: "200px 0px",
  });

  const productImage = useMemo(() => {
    return product.featuredImage ?? product.images.nodes[0];
  }, [product.featuredImage, product.images.nodes]);

  const minPrice = product.priceRange.minVariantPrice;
  const compareAtPrice = product.compareAtPriceRange.maxVariantPrice;

  const discount = useMemo(() => {
    if (!isDiscounted(minPrice, compareAtPrice)) return null;
    return calculateDiscount(minPrice, compareAtPrice);
  }, [minPrice, compareAtPrice]);

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex w-full min-w-50 max-w-75 flex-col items-start gap-3",
        productItemClassName,
        inView ? "opacity-100" : "opacity-0",
        "transition-opacity duration-400"
      )}
    >
      {discount !== null && discount > 0 && (
        <span className="absolute right-3 top-3 z-1 rounded-full bg-primary px-2 py-1 font-primary text-micro font-bold text-white">
          -{discount}%
        </span>
      )}
      <Link
        to="/$locale/product/$handle"
        params={{ locale, handle: product.handle }}
        className="w-full"
        viewTransition
      >
        <div
          className={cn(
            "overflow-hidden rounded-xl bg-transparent",
            "aspect-3/4"
          )}
          style={{ viewTransitionName: `product-image-${product.handle}` }}
        >
          <Image
            data={productImage}
            alt={productImage.altText ?? product.title}
            width={imageWidth}
            height={imageHeight}
            className={cn(
              "h-full w-full object-cover transition-transform duration-300 hover:scale-105",
              productImageClassName
            )}
            loading={imageFetchPriority === "eager" ? "eager" : "lazy"}
            sizes="(min-width: 1024px) 300px, (min-width: 768px) 300px, 50vw"
          />
        </div>
      </Link>
      <div className="flex w-full flex-col gap-1">
        <Link
          to="/$locale/product/$handle"
          params={{ locale, handle: product.handle }}
          viewTransition
        >
          <h3
            className={cn(
              "font-secondary text-headline-small font-medium -tracking-[0.02em] text-text-1",
              productItemTitleClassName
            )}
          >
            {product.title}
          </h3>
        </Link>
        <PriceDisplay
          product={product}
          productPriceClassName={productPriceClassName}
        />
        <BasePriceLine
          basePrice={cheapestVariantBasePrice(basePriceVariants)}
        />
      </div>
    </div>
  );
};
